import React, { useEffect, useState } from "react";
import {
  Navbar,
  Nav,
  NavItem,
  NavLink,
  UncontrolledDropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  Container,
  NavbarToggler,
  Collapse,
} from "reactstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { handleLogout } from "../../redux/authentication";
import { FaArrowUp } from "react-icons/fa";
import { motion } from "framer-motion";

const Layout = ({ children }) => {
  const [showScroll, setShowScroll] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.authentication);

  const layout = auth?.layoutData?.data?.[0] || {};
  const brandNavy = "#344e66";
  const amberAccent = "#ffc107";
  const userRole = auth?.userData?.rights;
  const branchData = auth?.branchData || {};
  const currentPath = location.pathname;

  const logoutHandler = () => {
    dispatch(handleLogout());
    sessionStorage.clear();
    localStorage.clear();
    navigate("/", { replace: true });
  };

  useEffect(() => {
    const handleScroll = () => setShowScroll(window.scrollY > 200);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const hasAnyRight = (roles = []) => {
    if (!roles || roles.length === 0) return true;
    return roles.includes(userRole);
  };

  const isLinkActive = (targetPath) => {
    if (!targetPath) return false;
    return currentPath === targetPath;
  };

  const renderMenuItems = () => {
    if (!layout?.menuItems) return null;

    return layout.menuItems.map((menu, index) => {
      if (!hasAnyRight(menu.roles)) return null;

      const isFirstItem = index === 0;
      const navItemClass = isFirstItem ? "ms-auto" : "";

      if (menu.type === "item") {
        const active = isLinkActive(menu.path);
        return (
          <NavItem key={index} className={navItemClass}>
            <NavLink
              tag={Link}
              to={menu.path}
              className={`text-white px-3 py-2 ${active ? "active fw-bold" : ""}`}
              style={{
                color: active ? amberAccent : "#ffffff",
                borderBottom: active ? `2px solid ${amberAccent}` : "none",
                transition: "color 0.2s ease",
              }}
            >
              {menu.label}
            </NavLink>
          </NavItem>
        );
      }

      const isChildActive = menu.children?.some((child) => isLinkActive(child.path));

      return (
        <UncontrolledDropdown nav inNavbar key={index} className={navItemClass}>
          <DropdownToggle
            nav
            caret
            className={`px-3 py-2 ${isChildActive ? "active fw-bold" : ""}`}
            style={{
              color: isChildActive ? amberAccent : "#ffffff",
              borderBottom: isChildActive ? `2px solid ${amberAccent}` : "none",
            }}
          >
            {menu.label}
          </DropdownToggle>
          <DropdownMenu end className="shadow border-0">
            {menu.children?.map((child, cIdx) => {
              if (child.divider) return <DropdownItem divider key={cIdx} />;
              if (child.roles && !hasAnyRight(child.roles)) return null;
              const childActive = isLinkActive(child.path);
              return (
                <DropdownItem
                  key={cIdx}
                  className={childActive ? "fw-bold text-warning bg-light" : ""}
                  onClick={() => child.path && navigate(child.path)}
                >
                  {child.label}
                </DropdownItem>
              );
            })}
          </DropdownMenu>
        </UncontrolledDropdown>
      );
    });
  };

  return (
    <div className="d-flex flex-column min-vh-100">
      <Navbar
        expand="md"
        className="navbar shadow-sm py-2"
        style={{
          backgroundColor: brandNavy,
          borderBottom: `2px solid ${amberAccent}`,
        }}
      >
        <Container>
          <div
            className="navbar-brand fw-bold d-flex align-items-center text-white"
            style={{ letterSpacing: "1px", cursor: "pointer" }}
            onClick={() => navigate("/")}
          >
            <img
              src={
                branchData?.logoUrl ||
                "https://paupet-pub.s3.eu-north-1.amazonaws.com/logo512.png"
              }
              width="32"
              height="32"
              className="me-2 rounded-circle border border-warning"
              alt="Logo"
            />
            <span>
              {layout?.brandTitle?.default ||
                branchData.LegalName ||
                "PAUPET SOFT"}
            </span>
          </div>

          <NavbarToggler
            className="navbar-dark border-light"
            onClick={() => setIsOpen(!isOpen)}
          />

          <Collapse isOpen={isOpen} navbar>
            <Nav className="ms-auto align-items-center" navbar>
              {renderMenuItems()}
            </Nav>

            {/* User info & Logout section */}
            {auth.userData?.userId && (
              <NavItem className="d-flex align-items-center mx-3 my-2 my-md-0">
                <span className="badge bg-secondary text-light px-2 py-1">
                  User: {auth.userData.userId}
                </span>
              </NavItem>
            )}

            {auth.userData?.userId && (
              <NavItem>
                <NavLink
                  className="fw-bold px-3 py-1 rounded"
                  style={{
                    color: amberAccent,
                    cursor: "pointer",
                    border: `1px solid ${amberAccent}`,
                  }}
                  onClick={logoutHandler}
                >
                  Logout
                </NavLink>
              </NavItem>
            )}
          </Collapse>
        </Container>
      </Navbar>

      <main className="flex-grow-1 p-3 p-md-4 bg-light">{children}</main>

      {/* Scroll to top floating button */}
      {showScroll && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="btn btn-warning position-fixed bottom-0 end-0 m-4 rounded-circle shadow"
          style={{ width: "45px", height: "45px", zIndex: 1000 }}
        >
          <FaArrowUp />
        </motion.button>
      )}

      <footer
        className="text-center py-3 mt-auto shadow-inner"
        style={{ backgroundColor: brandNavy, color: "#e0e0e0" }}
      >
        <Container>
          <p className="small mb-0">
            {layout?.footer?.copyright ||
              `All rights reserved © ${new Date().getFullYear()} PAUPET SOFT`}
          </p>
        </Container>
      </footer>
    </div>
  );
};

export default Layout;